import React from 'react';
import * as Icons from 'lucide-react';

// Lucide-name lookup (kebab-case → PascalCase). Falls back to Sparkles.
const iconFor = (name) => {
  if (!name) return Icons.Sparkles;
  const key = String(name)
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
    .join('');
  return Icons[key] || Icons.Sparkles;
};

const SectionHeader = ({ eyebrow, title, subtitle, accent = '#d4af37', align = 'left', onDark = false }) => (
  <div
    className={`max-w-3xl mb-14 ${align === 'center' ? 'mx-auto text-center' : ''}`}
    data-testid="designer-section-header"
  >
    {eyebrow && (
      <div
        className="text-xs font-bold uppercase tracking-[0.28em] mb-4"
        style={{ color: accent }}
      >
        {eyebrow}
      </div>
    )}
    {title && (
      <h2 className={`text-3xl md:text-5xl font-bold leading-tight tracking-tight ${onDark ? 'text-white' : 'text-[#0a1a2f]'}`}>
        {title}
      </h2>
    )}
    {subtitle && (
      <p className={`mt-4 text-base md:text-lg leading-relaxed ${onDark ? 'text-white/75' : 'text-slate-600'}`}>
        {subtitle}
      </p>
    )}
  </div>
);

// Resolve section background to inline style + text-color hint.
// Returns { style, onDark, overlayStyle? }
const resolveBackground = (bg, defaults = { fallback: '#ffffff', onDark: false }) => {
  if (!bg || bg.type === 'none' || !bg.type) {
    return { style: { backgroundColor: defaults.fallback }, onDark: defaults.onDark };
  }
  if (bg.type === 'color') {
    return {
      style: { backgroundColor: bg.color || defaults.fallback },
      onDark: bg.dark_text ? false : true,
    };
  }
  if (bg.type === 'image' && bg.image) {
    const overlay = typeof bg.overlay === 'number' ? bg.overlay : 40;
    return {
      style: {
        backgroundImage: `linear-gradient(rgba(10,26,47,${overlay/100}), rgba(10,26,47,${overlay/100})), url(${bg.image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      },
      onDark: true,
    };
  }
  return { style: { backgroundColor: defaults.fallback }, onDark: defaults.onDark };
};

// ── 1. Feature Grid — Diagonal cascade ──────────────────────────────
export const FeatureGridDiagonalSection = ({ content }) => {
  if (!content) return null;
  const { eyebrow, title, subtitle, accent_color, background, boxes = [] } = content;
  const accent = accent_color || '#d4af37';
  const bg = resolveBackground(background, { fallback: '#ffffff', onDark: false });
  const offsets = ['md:mt-0', 'md:mt-10', 'md:mt-20', 'md:mt-32', 'md:mt-40', 'md:mt-48'];
  return (
    <section className="py-24 overflow-hidden" style={bg.style} data-testid="section-feature-grid-diagonal">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHeader eyebrow={eyebrow} title={title} subtitle={subtitle} accent={accent} onDark={bg.onDark} />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-4 relative">
          {/* Decorative accent bar */}
          <div
            className="hidden lg:block absolute top-1/2 left-0 right-0 h-px -z-10"
            style={{ background: `linear-gradient(90deg, transparent, ${accent}55, transparent)` }}
          />
          {boxes.map((box, idx) => {
            const Icon = iconFor(box.icon);
            return (
              <div
                key={idx}
                className={`group relative p-6 md:p-7 rounded-2xl bg-white border border-slate-200/70 shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1 ${offsets[idx % offsets.length]}`}
                data-testid={`feature-diagonal-card-${idx}`}
              >
                <div
                  className="absolute -top-4 left-6 w-12 h-12 rounded-xl flex items-center justify-center shadow-lg transition-transform duration-500 group-hover:rotate-6"
                  style={{ backgroundColor: accent, color: '#0a1a2f' }}
                >
                  <Icon className="w-6 h-6" strokeWidth={2.25} />
                </div>
                <div className="text-[10px] font-bold text-slate-400 tracking-widest mt-4">
                  0{idx + 1}
                </div>
                <h3 className="text-xl font-semibold text-[#0a1a2f] mt-2">{box.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{box.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ── 2. Bento Layout — asymmetric bento grid ─────────────────────────
export const BentoLayoutSection = ({ content }) => {
  if (!content) return null;
  const { eyebrow, title, subtitle, accent_color, background, boxes = [] } = content;
  const accent = accent_color || '#00457f';
  const bg = resolveBackground(background, { fallback: '#f8fafc', onDark: false });
  const [hero, ...rest] = boxes;
  if (!hero) return null;
  const HeroIcon = iconFor(hero.icon);
  return (
    <section className="py-24" style={bg.style} data-testid="section-bento-layout">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHeader eyebrow={eyebrow} title={title} subtitle={subtitle} accent={accent} onDark={bg.onDark} />
        <div className="grid grid-cols-1 md:grid-cols-3 md:grid-rows-3 gap-4 md:gap-5 auto-rows-fr">
          {/* Hero bento card — spans 2 cols and all 3 rows */}
          <div
            className="md:col-span-2 md:row-span-3 relative overflow-hidden rounded-3xl p-8 md:p-12 text-white flex flex-col justify-between min-h-[320px] md:min-h-[560px]"
            style={{
              background: `linear-gradient(135deg, ${accent} 0%, #0a1a2f 100%)`,
            }}
            data-testid="bento-hero"
          >
            {/* Decorative blob */}
            <div
              className="absolute -bottom-12 -right-12 w-64 h-64 rounded-full opacity-20 blur-3xl"
              style={{ backgroundColor: accent }}
            />
            <div className="relative flex items-center justify-between">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center backdrop-blur-md bg-white/15 border border-white/20"
              >
                <HeroIcon className="w-7 h-7" strokeWidth={2.25} />
              </div>
              <span className="text-[10px] font-bold tracking-[0.3em] uppercase text-white/60">Featured</span>
            </div>
            <div className="relative">
              <h3 className="text-2xl md:text-4xl font-bold leading-tight">{hero.title}</h3>
              <p className="mt-4 text-white/80 text-base md:text-lg max-w-md leading-relaxed">
                {hero.description}
              </p>
            </div>
          </div>
          {/* Supporting cards */}
          {rest.slice(0, 3).map((box, idx) => {
            const Icon = iconFor(box.icon);
            return (
              <div
                key={idx}
                className="relative rounded-3xl p-6 bg-white border border-slate-200/70 shadow-sm hover:shadow-lg transition-all duration-300 hover:border-slate-300 flex flex-col justify-between min-h-[180px]"
                data-testid={`bento-card-${idx}`}
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${accent}12`, color: accent }}
                >
                  <Icon className="w-5 h-5" strokeWidth={2.25} />
                </div>
                <div>
                  <h4 className="text-base font-semibold text-[#0a1a2f]">{box.title}</h4>
                  <p className="mt-1 text-sm text-slate-600 leading-snug">{box.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ── 3. Step Timeline — vertical numbered connected steps ────────────
export const StepTimelineSection = ({ content }) => {
  if (!content) return null;
  const { eyebrow, title, subtitle, accent_color, background, boxes = [] } = content;
  const accent = accent_color || '#00457f';
  const bg = resolveBackground(background, { fallback: '#ffffff', onDark: false });
  return (
    <section className="py-24" style={bg.style} data-testid="section-step-timeline">
      <div className="max-w-5xl mx-auto px-6">
        <SectionHeader eyebrow={eyebrow} title={title} subtitle={subtitle} accent={accent} align="center" onDark={bg.onDark} />
        <ol className="relative mt-16">
          {/* Vertical connecting line */}
          <span
            className="absolute left-8 md:left-1/2 top-2 bottom-2 w-px"
            style={{ background: `linear-gradient(180deg, ${accent}22 0%, ${accent}88 50%, ${accent}22 100%)` }}
            aria-hidden
          />
          {boxes.map((box, idx) => {
            const Icon = iconFor(box.icon);
            const isRight = idx % 2 === 1;
            return (
              <li
                key={idx}
                className="relative pb-12"
                data-testid={`timeline-step-${idx}`}
              >
                {/* Number badge — absolute-centered on md+ */}
                <div className="absolute left-0 md:left-1/2 md:-translate-x-1/2 top-0 z-10">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center text-white text-lg font-bold shadow-lg border-4 border-white"
                    style={{ backgroundColor: accent }}
                  >
                    {String(idx + 1).padStart(2, '0')}
                  </div>
                </div>
                {/* Card — mobile full-width; on md+ takes half width offset from centerline */}
                <div
                  className={`pl-20 md:pl-0 md:w-1/2 ${
                    isRight
                      ? 'md:ml-auto md:pl-16'
                      : 'md:pr-16 md:text-right'
                  }`}
                >
                  <div
                    className={`inline-flex items-center gap-3 ${isRight ? '' : 'md:flex-row-reverse'}`}
                    style={{ color: accent }}
                  >
                    <Icon className="w-5 h-5" strokeWidth={2.25} />
                    <span className="text-xs font-bold uppercase tracking-widest">Step {idx + 1}</span>
                  </div>
                  <h3 className={`mt-2 text-xl md:text-2xl font-semibold ${bg.onDark ? 'text-white' : 'text-[#0a1a2f]'}`}>{box.title}</h3>
                  <p className={`mt-2 text-sm md:text-base leading-relaxed ${bg.onDark ? 'text-white/75' : 'text-slate-600'} ${isRight ? '' : 'md:ml-auto'}`}>
                    {box.description}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
};

// ── 4. Layered Panels — overlapping 2×2 grid with depth ──────────────
export const LayeredPanelsSection = ({ content }) => {
  if (!content) return null;
  const { eyebrow, title, subtitle, accent_color, background, boxes = [] } = content;
  const accent = accent_color || '#d4af37';
  // Default this variant to the dark navy for the layered-shadow look.
  const bg = resolveBackground(background, { fallback: '#0a1a2f', onDark: true });
  return (
    <section className="py-24 relative overflow-hidden" style={bg.style} data-testid="section-layered-panels">
      {/* Background accent band */}
      <div
        className="absolute top-1/4 left-0 right-0 h-1/2 opacity-[0.06]"
        style={{ backgroundColor: accent }}
        aria-hidden
      />
      <div className="max-w-6xl mx-auto px-6 relative">
        <SectionHeader
          eyebrow={eyebrow}
          title={title}
          subtitle={subtitle}
          accent={accent}
          align="center"
          onDark={bg.onDark}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mt-16">
          {boxes.slice(0, 4).map((box, idx) => {
            const Icon = iconFor(box.icon);
            // Alternating offset for layered look
            const offset = idx % 2 === 0 ? 'md:translate-y-0' : 'md:translate-y-10';
            return (
              <div
                key={idx}
                className={`relative group ${offset} transition-transform duration-500`}
                data-testid={`layered-panel-${idx}`}
              >
                {/* Back shadow layer */}
                <div
                  className="absolute inset-0 rounded-2xl translate-x-2 translate-y-2 group-hover:translate-x-3 group-hover:translate-y-3 transition-transform duration-500"
                  style={{ backgroundColor: accent, opacity: 0.35 }}
                  aria-hidden
                />
                {/* Front card */}
                <div className="relative rounded-2xl p-8 bg-white shadow-2xl border border-white/10">
                  <div className="flex items-start gap-4">
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0"
                      style={{ backgroundColor: `${accent}18`, color: accent }}
                    >
                      <Icon className="w-7 h-7" strokeWidth={2.25} />
                    </div>
                    <div>
                      <div className="text-[10px] font-bold text-slate-400 tracking-widest">
                        PILLAR 0{idx + 1}
                      </div>
                      <h3 className="text-xl md:text-2xl font-bold text-[#0a1a2f] mt-1">
                        {box.title}
                      </h3>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-slate-600 leading-relaxed">{box.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ── 5. Layered Panels — 6 (3×2 with per-column offset & dual-tone shadows) ──
export const LayeredPanels6Section = ({ content }) => {
  if (!content) return null;
  const { eyebrow, title, subtitle, accent_color, background, boxes = [] } = content;
  const accent = accent_color || '#d4af37';
  const bg = resolveBackground(background, { fallback: '#0a1a2f', onDark: true });
  // Per-column vertical offset creates a rhythmic cascade
  const colOffset = ['md:mt-0', 'md:mt-8', 'md:mt-16'];
  // Alternate shadow color (accent, then a cool tint) for dual-tone depth
  const shadowFor = (idx) => (idx % 2 === 0 ? accent : '#00457f');
  return (
    <section className="py-24 relative overflow-hidden" style={bg.style} data-testid="section-layered-panels-6">
      <div
        className="absolute top-1/3 left-0 right-0 h-1/3 opacity-[0.05]"
        style={{ backgroundColor: accent }}
        aria-hidden
      />
      <div className="max-w-6xl mx-auto px-6 relative">
        <SectionHeader
          eyebrow={eyebrow}
          title={title}
          subtitle={subtitle}
          accent={accent}
          align="center"
          onDark={bg.onDark}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-16">
          {boxes.slice(0, 6).map((box, idx) => {
            const Icon = iconFor(box.icon);
            const col = idx % 3;
            const shadow = shadowFor(idx);
            return (
              <div
                key={idx}
                className={`relative group ${colOffset[col]} transition-transform duration-500`}
                data-testid={`layered-panel-6-${idx}`}
              >
                <div
                  className="absolute inset-0 rounded-2xl translate-x-2 translate-y-2 group-hover:translate-x-3 group-hover:translate-y-3 transition-transform duration-500"
                  style={{ backgroundColor: shadow, opacity: 0.35 }}
                  aria-hidden
                />
                <div className="relative rounded-2xl p-6 bg-white shadow-xl border border-slate-100 h-full">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${shadow}18`, color: shadow }}
                  >
                    <Icon className="w-6 h-6" strokeWidth={2.25} />
                  </div>
                  <div className="text-[10px] font-bold text-slate-400 tracking-widest">
                    0{idx + 1}
                  </div>
                  <h3 className="mt-1 text-lg md:text-xl font-bold text-[#0a1a2f]">{box.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{box.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ── 6. Layered Panels — 8 (4×2 cascading with rotating hover) ──────
export const LayeredPanels8Section = ({ content }) => {
  if (!content) return null;
  const { eyebrow, title, subtitle, accent_color, background, boxes = [] } = content;
  const accent = accent_color || '#d4af37';
  const bg = resolveBackground(background, { fallback: '#0a1a2f', onDark: true });
  // Cascading vertical offset by column
  const colOffset = ['md:mt-0', 'md:mt-6', 'md:mt-12', 'md:mt-16'];
  return (
    <section className="py-24 relative overflow-hidden" style={bg.style} data-testid="section-layered-panels-8">
      <div
        className="absolute top-1/4 left-0 right-0 h-1/2 opacity-[0.05]"
        style={{
          background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
        }}
        aria-hidden
      />
      <div className="max-w-7xl mx-auto px-6 relative">
        <SectionHeader
          eyebrow={eyebrow}
          title={title}
          subtitle={subtitle}
          accent={accent}
          align="center"
          onDark={bg.onDark}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-16">
          {boxes.slice(0, 8).map((box, idx) => {
            const Icon = iconFor(box.icon);
            const col = idx % 4;
            return (
              <div
                key={idx}
                className={`relative group ${colOffset[col]} transition-all duration-500`}
                data-testid={`layered-panel-8-${idx}`}
              >
                {/* Gradient shadow with slight rotation */}
                <div
                  className="absolute inset-0 rounded-2xl translate-x-1.5 translate-y-1.5 group-hover:translate-x-2 group-hover:translate-y-2 group-hover:rotate-1 transition-transform duration-500 origin-center"
                  style={{
                    background: `linear-gradient(135deg, ${accent} 0%, #00457f 100%)`,
                    opacity: 0.4,
                  }}
                  aria-hidden
                />
                <div className="relative rounded-2xl p-5 bg-white shadow-xl border border-slate-100 h-full group-hover:-rotate-1 transition-transform duration-500 origin-center">
                  <div className="flex items-start justify-between">
                    <div
                      className="w-11 h-11 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${accent}18`, color: accent }}
                    >
                      <Icon className="w-5 h-5" strokeWidth={2.25} />
                    </div>
                    <span className="text-[10px] font-bold text-slate-300 tracking-widest">
                      0{idx + 1}
                    </span>
                  </div>
                  <h3 className="mt-4 text-base md:text-lg font-bold text-[#0a1a2f]">{box.title}</h3>
                  <p className="mt-2 text-xs md:text-sm text-slate-600 leading-relaxed">{box.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ── 7. Custom Layout — freeform admin-built canvas ─────────────────
export const CustomLayoutSection = ({ content }) => {
  if (!content) return null;
  const {
    eyebrow,
    section_title,
    section_subtitle,
    accent_color,
    background,
    canvas_width = 1200,
    canvas_height = 600,
    boxes = [],
  } = content;
  const accent = accent_color || '#d4af37';
  const bg = resolveBackground(background, { fallback: '#ffffff', onDark: false });
  return (
    <section className="py-20" style={bg.style} data-testid="section-custom-layout">
      <div className="max-w-7xl mx-auto px-6">
        {(eyebrow || section_title || section_subtitle) && (
          <SectionHeader
            eyebrow={eyebrow}
            title={section_title}
            subtitle={section_subtitle}
            accent={accent}
            align="center"
            onDark={bg.onDark}
          />
        )}
        {/* Aspect-ratio wrapper preserves the admin's canvas proportions. */}
        <div
          className="relative w-full mx-auto mt-8"
          style={{
            aspectRatio: `${canvas_width} / ${canvas_height}`,
          }}
        >
          {/* Inner scaled canvas — absolute positions map from admin canvas units. */}
          <div
            className="absolute inset-0"
            style={{
              width: canvas_width,
              height: canvas_height,
              transformOrigin: 'top left',
              // Scale to fit width; use CSS scale via CSS variables so it stays responsive.
            }}
            ref={(el) => {
              if (!el || !el.parentElement) return;
              const parentW = el.parentElement.clientWidth;
              const scale = parentW / canvas_width;
              el.style.transform = `scale(${scale})`;
            }}
          >
            {boxes.map((box) => {
              const Icon = iconFor(box.icon);
              const isText = box.variant === 'text';
              const align = box.text_align || (isText ? 'center' : 'left');
              const alignCls = align === 'center' ? 'items-center text-center' : align === 'right' ? 'items-end text-right' : 'items-start text-left';
              return (
                <div
                  key={box.id}
                  data-testid={`custom-panel-${box.id}`}
                  style={{
                    position: 'absolute',
                    left: box.x,
                    top: box.y,
                    width: box.w,
                    height: box.h,
                    transform: `rotate(${box.rotation || 0}deg)`,
                    backgroundColor: isText ? 'transparent' : (box.bg_color || '#ffffff'),
                    color: box.text_color || '#0a1a2f',
                    borderRadius: isText ? 0 : (box.border_radius ?? 12),
                    boxShadow: !isText && box.shadow !== false ? '0 20px 40px -12px rgba(10,26,47,0.25)' : 'none',
                    zIndex: box.z || 1,
                    padding: isText ? 8 : 20,
                    overflow: 'hidden',
                  }}
                >
                  <div className={`h-full flex flex-col gap-2 justify-center ${alignCls}`}>
                    {!isText && box.show_icon !== false && box.icon && (
                      <div
                        className="w-11 h-11 rounded-lg flex items-center justify-center"
                        style={{
                          backgroundColor: `${box.accent_color || accent}22`,
                          color: box.accent_color || accent,
                        }}
                      >
                        <Icon className="w-5 h-5" strokeWidth={2.25} />
                      </div>
                    )}
                    {box.title && (
                      <div
                        className="font-semibold leading-tight"
                        style={{ fontSize: isText ? (box.title_size || 32) : 16 }}
                      >
                        {box.title}
                      </div>
                    )}
                    {box.description && (
                      <div
                        className="opacity-80 leading-relaxed"
                        style={{ fontSize: isText ? 15 : 14 }}
                      >
                        {box.description}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

// ── Router: pick the right component by section_type ────────────────
export const DESIGNER_SECTION_TYPES = new Set([
  'feature_grid_diagonal',
  'bento_layout',
  'step_timeline',
  'layered_panels',
  'layered_panels_6',
  'layered_panels_8',
  'custom_layout',
]);

export const DesignerSection = ({ section }) => {
  if (!section) return null;
  switch (section.section_type) {
    case 'feature_grid_diagonal':
      return <FeatureGridDiagonalSection content={section.content} />;
    case 'bento_layout':
      return <BentoLayoutSection content={section.content} />;
    case 'step_timeline':
      return <StepTimelineSection content={section.content} />;
    case 'layered_panels':
      return <LayeredPanelsSection content={section.content} />;
    case 'layered_panels_6':
      return <LayeredPanels6Section content={section.content} />;
    case 'layered_panels_8':
      return <LayeredPanels8Section content={section.content} />;
    case 'custom_layout':
      return <CustomLayoutSection content={section.content} />;
    default:
      return null;
  }
};
